import { Vue, Component, Prop, Watch, Emit } from 'vue-property-decorator';
import EditableTable from '@/../bower_components/teknisavuecomponents/components/grids/EditableTable.vue';
import Action from '@/../bower_components/teknisavuecomponents/assets/interfaces/Action';
import ToolbarAction from '@/../bower_components/teknisavuecomponents/assets/interfaces/ToolbarAction';
import Util from '@/../bower_components/teknisavuecomponents/assets/utils/Util';
import User from '@/../bower_components/teknisavuecomponents/assets/interfaces/User';
import SnackbarUtils from '@/../bower_components/teknisavuecomponents/assets/utils/SnackbarUtils';
import Periodo from '@/../bower_components/teknisavuecomponents/assets/interfaces/Periodo';
import DateUtil from '@/../bower_components/teknisavuecomponents/assets/utils/DateUtil';
import EditableTableColumn from '@/../bower_components/teknisavuecomponents/assets/interfaces/EditableTableColumn';
import ResumoController from '@/assets/ts/controller/ResumoController';
import Time from '@/../bower_components/teknisavuecomponents/assets/utils/Time';

@Component({
  name: 'SituFreqPopup',
  components: {
    EditableTable,
  },
})
class SituFreqPopup extends Vue {
  // #region [ PROPS ]
  @Prop({ default: null })
  private date: string;

  @Prop({ default: null })
  private nrVinculo: string;

  @Prop({ default: false })
  private value: boolean;
  // #endregion

  // #region [ DATA ]
  private readonly CARD_TITLE = 'Situação de Frequência';

  private readonly DEFAULT_ROW = {
    NRSITUFREQUENCIA: null,
    QTREFAPURSITUFREQ: null,
    NRRESUMOSITUFREQ: null,
  };

  private internalValue = false;
  private internalDate = null;

  private rows: any[] = [];
  private backup: any[] = [];
  private editing = false;
  private changed = false;

  private auxValid = true;

  private lastRequestId = 0;

  private fieldsData = {
    situacoesFreq: [] as any[],
  };

  private loadingItems = {
    reload: false,
    save: false,
    situacoesFreq: false,
    canEdit: false,
  };
  // #endregion

  // #region [ STORE ]
  private get user(): User {
    return this.$store.getters.user;
  }

  private get periodoDemonstrativoAtual(): Periodo {
    return this.$store.getters.periodoDemonstrativoAtual;
  }
  // #endregion

  // #region [ EVENTS ]
  @Emit('close')
  private emitClose(): boolean {
    return this.changed;
  }

  @Emit('input')
  private emitInput(): boolean {
    return this.internalValue;
  }
  // #endregion

  // #region [ COMPUTED ]
  public get cardTitle(): string {
    if (!this.date) {
      return this.CARD_TITLE;
    }

    const title = `${this.CARD_TITLE} - ${this.internalDate}`;

    return title;
  }

  public get isLoading(): boolean {
    const loading = Util.anyTrue(Object.values(this.loadingItems));
    return loading;
  }

  public get gridMode(): string {
    const mode = this.editing ?
      'edit' :
      'view';

    return mode;
  }

  private get canEdit(): boolean {
    if (this.editing) {
      return false;
    }

    if (this.user.dp) {
      return true;
    }

    const canEditResumo = this.user.idaltresumofreq === 'S';

    return canEditResumo;
  }

  private get nextDay(): string {
    const isLastDay =
      this.internalDate ===
      this.periodoDemonstrativoAtual.DTFINALAPURACAO;

    if (isLastDay || this.internalDate === null) {
      return null;
    }

    const next = DateUtil.addDaysDate(this.internalDate, 1);

    return next;
  }

  private get previousDay(): string {
    const isFirstDay =
      this.internalDate ===
      this.periodoDemonstrativoAtual.DTINICIALAPURACAO;

    if (isFirstDay || this.internalDate === null) {
      return null;
    }

    const previous = DateUtil.addDaysDate(this.internalDate, -1);

    return previous;
  }

  private get toolbarActions(): ToolbarAction[] {
    const actions: ToolbarAction[] = [
      {
        name: 'reload',
        label: 'Atualizar',
        icon: 'mdi-refresh',
        onClick: this.reload,
        condition: !this.editing,
      },
      {
        name: 'edit',
        label: 'Editar',
        icon: 'mdi-pencil',
        onClick: this.edit,
        condition: this.canEdit,
      },
      {
        name: 'save',
        label: 'Salvar',
        icon: 'mdi-content-save',
        onClick: this.save,
        condition: this.editing,
      },
      {
        name: 'add',
        label: 'Adicionar',
        icon: 'mdi-plus-circle',
        onClick: this.add,
        condition: this.editing,
      },
      {
        name: 'cancel',
        label: 'Cancelar',
        icon: 'mdi-close-circle',
        onClick: this.cancel,
        condition: this.editing,
      },
    ];

    return actions;
  }

  public get columns(): EditableTableColumn[] {
    const columns: EditableTableColumn[] = [
      {
        value: 'NRSITUFREQUENCIA',
        text: 'Situação de Freq.',
        type: 'autocomplete',
        descriptionField: 'DSSITUFREQUENCIA',
        selectDescription: 'DSSITUEVENTOS',
        valueField: 'NRSITUFREQUENCIA',
        width: 350,
        align: 'left',
        outData: {
          DSEVENTOS: 'DSEVENTOS',
        },
        onInput: this.nrSituOnInput,
        items: this.fieldsData.situacoesFreq,
        menuProps: {
          minWidth: 600,
          maxWidth: 600,
        }
      },
      {
        value: 'DSEVENTOS',
        text: 'Eventos',
        type: 'text',
        readonly: true,
        align: 'left',
        textAlign: 'left',
        width: 235,
      },
      {
        value: 'TEMPO',
        text: 'Tempo',
        type: 'text',
        align: 'end',
        textAlign: 'right',
        width: 90,
        onBlur: this.tempoOnBlur,
      },
    ];

    return columns;
  }

  private rowsAreValid(): boolean {
    const valid = this.rows.reduce((accumulator, row) => {
      const isValid = this.tempoIsValid(row.TEMPO);
      return accumulator && isValid;
    }, true);

    return valid;
  }
  // #endregion

  // #region [ LIFECYCLE ]
  private created() {
    this.loadSituacoesFreq();
  }
  // #endregion

  // #region [ WATCHERS ]
  @Watch('value', { immediate: true })
  private async valueOnChange() {
    this.internalValue = this.value;

    if (this.value) {
      this.changed = false;
      this.editing = false;

      await Util.setTimeout(10);
      this.reload();
    }
  }

  @Watch('date', { immediate: true })
  private dateOnChange() {
    this.internalDate = this.date;
  }
  // #endregion

  // #region [ METHODS ]
  private closeDialog() {
    this.internalValue = false;
    this.emitClose();
    this.emitInput();
  }

  private async reload() {
    this.rows = [];

    const requestId = ++this.lastRequestId;

    this.loadingItems.reload = true;

    try {
      const resumo = await ResumoController.getResumoFreq(
        this.nrVinculo,
        this.internalDate,
        false,
      );

      if (requestId === this.lastRequestId) {
        this.rows = this.getRowsWithTempo(resumo);
        this.setBackup();
      }
    } finally {
      if (requestId === this.lastRequestId) {
        this.loadingItems.reload = false;
      }
    }

  }

  private edit() {
    this.editing = true;
  }

  private add() {
    const newRow = Util.deepCopy(this.DEFAULT_ROW);
    this.rows.push(newRow);
  }

  private cancel() {
    const backupCopy = Util.deepCopy(this.backup);
    this.rows = backupCopy;

    this.editing = false;
  }

  private async save() {
    if (!this.auxValid) {
      return;
    }

    this.loadingItems.save = true;

    try {
      const rows = this.getRowsWithReference();

      await ResumoController.saveResumoFreq(
        this.nrVinculo,
        this.internalDate,
        rows,
        false
      );

      this.editing = false;
      this.changed = true;

      this.reload();
    } finally {
      this.loadingItems.save = false;
    }

  }

  private goToNextDay() {
    this.goToDay(true);
  }

  private goToPreviousDay() {
    this.goToDay(false);
  }

  private goToDay(next: boolean) {
    this.internalDate = next ?
      this.nextDay :
      this.previousDay;

    this.editing = false;
    this.reload();
  }

  private nrSituOnInput(row: any) {
    this.verifyRepeadSituFreq(row);
  }

  private verifyRepeadSituFreq(row: any): boolean {
    const repeatedSitu = this.rows.some((_row) => {
      const differentId = row.__id !== _row.__id;
      const sameSitu = row.NRSITUFREQUENCIA === _row.NRSITUFREQUENCIA;

      return differentId && sameSitu;
    });

    if (!repeatedSitu) {
      return true;
    }

    const internalRow = this.rows.find((_row) => {
      return row.__id === _row.__id;
    });

    setTimeout(() => {
      internalRow.NRSITUFREQUENCIA = null;
    }, 5);

    const message = 'Já existe um registro com essa situação de frequência!';
    SnackbarUtils.showMessage(message, SnackbarUtils.TYPE_ALERT);

    return false;
  }

  private async loadSituacoesFreq() {
    this.loadingItems.situacoesFreq = true;

    try {
      this.fieldsData.situacoesFreq = await ResumoController.getSituacoesFreq(false);
    } finally {
      this.loadingItems.situacoesFreq = false;
    }
  }

  private tempoOnBlur(row: any) {
    this.verifyTempo(row);
    this.formatTempo(row);
  }

  private verifyTempo(row: any): boolean {
    const tempo: string = row.TEMPO;
    const isValid = this.tempoIsValid(tempo);

    if (!isValid) {
      const message = 'O tempo informado não é válido!';
      SnackbarUtils.showMessage(message, SnackbarUtils.TYPE_ALERT);
    }

    return isValid;
  }

  private tempoIsValid(
    tempo: string,
  ): boolean {
    if (Util.isEmptyOrBlank(tempo)) {
      return true;
    }

    const colonCount = Util.countSubstring(tempo, ':');

    if (colonCount >= 2) {
      return false;
    }

    const hasLetter = Util.hasLetter(tempo);

    if (hasLetter) {
      return false;
    }

    const qtMinutes = parseInt(tempo.split(':')[1], 10);

    // funciona mesmo se for NaN
    if (qtMinutes > 59) {
      return false;
    }

    return true;
  }

  private async formatTempo(
    row: any
  ) {
    const tempo: string = row.TEMPO;
    const isValid = this.tempoIsValid(tempo);

    const formattedTempo = this.tempoIsValid(tempo) && !Util.isEmptyOrBlank(tempo) ?
      Time.fromString(tempo).toString() :
      null;

    await Util.setTimeout(5);
    row.TEMPO = formattedTempo;
    this.auxValid = isValid;

    await Util.setTimeout(100);
    this.auxValid = true;
  }

  private setBackup() {
    this.backup = Util.deepCopy(this.rows);
  }

  private referenceToTime(
    reference: number | string
  ): string {
    const time = Time.fromFloat(reference);

    if (time === null) {
      return null;
    }

    const timeString = time.toString('HH:MM', true);

    return timeString;
  }

  private timeToReference(
    time: string
  ): number {
    if (Util.isEmptyOrBlank(time)) {
      return null;
    }

    const reference = Time.fromString(time).toFloat();

    return reference;
  }

  private getRowsWithTempo(
    rows: any[]
  ): any[] {
    const rowsCopy = Util.deepCopy(rows);

    rowsCopy.forEach((row) => {
      const reference: string = row.QTREFAPURSITUFREQ;
      const time = this.referenceToTime(reference);
      row.TEMPO = time;
    });

    return rowsCopy;
  }

  private getRowsWithReference(): any[] {
    const rowsCopy = Util.deepCopy(this.rows);

    rowsCopy.forEach((row) => {
      const time: string = row.TEMPO;
      const reference = this.timeToReference(time);
      row.QTREFAPURSITUFREQ = reference;
    });

    return rowsCopy;
  }
  // #endregion
}

export default SituFreqPopup;
